// SPDX-License-Identifier: AGPL-3.0-or-later

import type {PopoutAnimationType} from '@app/features/ui/popover';
import {EASE_OUT_QUART} from '@app/features/ui/utils/ReducedMotionAnimation';
import type {Placement} from '@floating-ui/react';
import type {Transition} from 'framer-motion';

const SMOOTH_OPEN_SECONDS = 0.16;

const SMOOTH_CLOSE_SECONDS = 0.12;

const PROFILE_SLIDE_SECONDS = 0.14;

const SMOOTH_SCALE_OFFSET = 0.98;

export function isProfileSlideAnimation(animationType: PopoutAnimationType): boolean {
	return animationType === 'profile-slide' || animationType === 'profile-slide-inverted';
}

export function getPopoutCloseDuration(animationType: PopoutAnimationType, prefersReducedMotion: boolean): number {
	if (prefersReducedMotion || animationType === 'none') {
		return 0;
	}
	return (isProfileSlideAnimation(animationType) ? PROFILE_SLIDE_SECONDS : SMOOTH_CLOSE_SECONDS) * 1000;
}

export function getPopoutMotion(
	animationType: PopoutAnimationType,
	shouldAnimate: boolean,
	isOpen: boolean,
): {
	initial: false | Record<string, number>;
	animate: Record<string, number>;
	transition: Transition;
} {
	if (!shouldAnimate || animationType === 'none') {
		return {
			initial: false as const,
			animate: {opacity: isOpen ? 1 : 0, x: 0, scale: 1},
			transition: {duration: 0},
		};
	}
	if (isProfileSlideAnimation(animationType)) {
		const slideOffset = animationType === 'profile-slide-inverted' ? -10 : 10;
		return {
			initial: {opacity: 0, x: slideOffset},
			animate: {opacity: isOpen ? 1 : 0, x: isOpen ? 0 : slideOffset},
			transition: {duration: PROFILE_SLIDE_SECONDS, ease: 'easeOut'},
		};
	}
	return {
		initial: {opacity: 0, scale: SMOOTH_SCALE_OFFSET},
		animate: {opacity: isOpen ? 1 : 0, scale: isOpen ? 1 : SMOOTH_SCALE_OFFSET, x: 0},
		transition: {duration: isOpen ? SMOOTH_OPEN_SECONDS : SMOOTH_CLOSE_SECONDS, ease: [...EASE_OUT_QUART]},
	};
}

// The scale grows out of the edge that faces the trigger, aligned like the placement.
export function getPopoutTransformOrigin(placement: Placement): string {
	const [side, align] = placement.split('-') as [string, string | undefined];
	const vertical = side === 'top' || side === 'bottom';
	const cross =
		align === 'start' ? (vertical ? 'left' : 'top') : align === 'end' ? (vertical ? 'right' : 'bottom') : 'center';
	const facing = {top: 'bottom', bottom: 'top', left: 'right', right: 'left'}[side] ?? 'center';
	return vertical ? `${cross} ${facing}` : `${facing} ${cross}`;
}
